import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { getUserTrainingOverviewForStaffing } from 'pollworker/src/fetchers';
import { apiDateConverter } from 'pollworker/src/utils/apiDateConverter';
import type { LmsEnrollmentStatus } from 'shared/src/utils/lmsStatus';
import type { TrainingDueDateMessage } from 'shared/src/utils/trainingStatus';
import {
  TrainingClassStatus,
  UnavailableReasonCode,
  getTrainingDueDateMessage as getSharedTrainingDueDateMessage,
  getTrainingStatusMessage as getSharedTrainingStatusMessage,
  getVoidReasonText,
} from 'shared/src/utils/trainingStatus';
import type { AppState } from './store';

export interface TrainingLmsOverlay {
  SessionId: string;
  LhCourseUuid: string;
  CourseName?: string;
  EnrollmentStatus?: LmsEnrollmentStatus;
  CompletedActivityCount?: number;
  TotalActivityCount?: number;
  RequirementIds?: string[];
}

export interface ClassSession {
  Id: string;
  Location: string;
  Schedule: string;
  ClassSize: number;
  FirstStart: string;
  FinalEnd: string;
  SeatsAvailable: number;
  IsAfterWorkSchedule: boolean;
  FirstWorkSchedule?: string;
  Lms?: TrainingLmsOverlay;
}

export interface UserTrainingClass {
  Id: string;
  ClassName: string;
  ContactName?: string;
  Phone?: string;
  Status: TrainingClassStatus;
  RequiredForRoles: string[];
  SessionId?: string;
  SessionDate?: string;
  SessionLocation?: string;
  SessionAddress?: string;
  CompletedDate?: string;
  DueDate?: string;
  AvailableSessionCount: number;
  AvailableSessions: ClassSession[];
  HasApplicableSessions: boolean;
  SelectedSession?: ClassSession;
  RequirementId?: string[];
  IsVoided: boolean;
  VoidReason?: number;
  IsExtraTraining: boolean;
  UnavailableReason?: UnavailableReasonCode;
  AttendanceNotRecorded: boolean;
  IsLegacy?: boolean;
}

export { TrainingClassStatus, UnavailableReasonCode, getVoidReasonText };

export function getTrainingStatusMessage(tc: UserTrainingClass): string | null {
  return getSharedTrainingStatusMessage({
    status: tc.Status,
    attendanceNotRecorded: tc.AttendanceNotRecorded,
    sessionDate: tc.SessionDate,
    availableSessionCount: tc.AvailableSessionCount,
    unavailableReason: tc.UnavailableReason,
    voidReason: tc.VoidReason,
    formatSessionDate: (value) => dayjs(apiDateConverter(value)).format('MMM D [at] h:mm A'),
  });
}

export function getTrainingDueDate(tc: UserTrainingClass, now?: Date): TrainingDueDateMessage | null {
  if (!tc.DueDate) return null;

  const isoDate = apiDateConverter(tc.DueDate) ?? tc.DueDate;

  return getSharedTrainingDueDateMessage({
    dueDate: new Date(isoDate),
    now,
    formatDueDate: (value) => dayjs(value).format('MMM D, YYYY'),
  });
}

export interface TrainingState {
  classes: UserTrainingClass[];
  loading: boolean;
}

const initialState: TrainingState = {
  classes: [],
  loading: false,
};

export const loadTrainingOverview = createAsyncThunk('training/loadOverview', (electionId: string) => getUserTrainingOverviewForStaffing(electionId));

const slice = createSlice({
  name: 'training',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadTrainingOverview.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadTrainingOverview.fulfilled, (state, action) => {
        state.classes = action.payload || [];
        state.loading = false;
      })
      .addCase(loadTrainingOverview.rejected, (state) => {
        state.classes = [];
        state.loading = false;
      });
  },
});

export const selectTrainingClasses = (state: AppState) => state.training.classes;
export const selectTrainingLoading = (state: AppState) => state.training.loading;

export const selectNeedsActionCount = createSelector(selectTrainingClasses, (classes) => classes?.filter((c) => c.Status === TrainingClassStatus.NeedsAction).length);

export default slice.reducer;
